/**
 * PRD-017 §6.6 programmatic rules, pure parts. The API wraps these with the reads and writes;
 * these functions decide, they never touch the database.
 *
 *   instantiate_checklist → planInstantiation
 *   evaluate_dependencies → findCycle (at insert), dependencyReason
 *   recompute_percent     → percentComplete
 *   derive_parent_status  → deriveParent (agent-standards #1, PRD-017 §3.15)
 */

import { shiftDate } from '../period/localDate'

/**
 * PRD-017 §3.3 / §3.7 (QA-32, ADR 0030). The four close cadences are period-bound: the row is stamped once
 * onto the month that closes it. The three recurring cadences are day-bound: the row is stamped by the clock
 * for each day it names, outside the period model (ADR 0016), and never counts toward a period's close.
 */
export type CloseCadence = 'monthly' | 'quarterly' | 'annually' | 'once'
export type RecurringCadence = 'daily' | 'weekly' | 'biweekly'
export type Cadence = CloseCadence | RecurringCadence
export const CLOSE_CADENCES: ReadonlyArray<CloseCadence> = ['monthly', 'quarterly', 'annually', 'once']
export const RECURRING_CADENCES: ReadonlyArray<RecurringCadence> = ['daily', 'weekly', 'biweekly']
export const CADENCES: ReadonlyArray<Cadence> = [...CLOSE_CADENCES, ...RECURRING_CADENCES]

export function isRecurringCadence(c: Cadence): c is RecurringCadence {
  return c === 'daily' || c === 'weekly' || c === 'biweekly'
}

/**
 * ADR 0030 (Sticky): the three visible classes of work. `close` = a template row with a close cadence, bound to a
 * period, drives period status. `recurring` = a template row with a recurring cadence, bound to a day, never part
 * of a period or the group close. `task` = person-created (kind task / request), any due date. Stored on
 * `work_item.work_class` at creation so every list filters on one indexed column.
 */
export type WorkClass = 'close' | 'recurring' | 'task'
export const WORK_CLASSES: ReadonlyArray<WorkClass> = ['close', 'recurring', 'task']

/** The class a template row's items carry. */
export function workClassForCadence(c: Cadence): 'close' | 'recurring' {
  return isRecurringCadence(c) ? 'recurring' : 'close'
}

// ---------------------------------------------------------------------------------------------
// Weekday mask (QA-32): which days a daily / weekly / biweekly row is stamped on.
// ---------------------------------------------------------------------------------------------

/** ISO weekday order, Monday first. Bit i of a mask is WEEKDAYS[i]. */
export const WEEKDAYS = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'] as const
export type Weekday = typeof WEEKDAYS[number]
/** Monday to Friday: the default for a new recurring row. */
export const WEEKDAY_MASK_WEEKDAYS = 0b0011111
export const WEEKDAY_MASK_ALL = 0b1111111

export function weekdayBit(day: Weekday): number {
  return 1 << WEEKDAYS.indexOf(day)
}

export function maskFromWeekdays(days: ReadonlyArray<Weekday>): number {
  return days.reduce((m, d) => m | weekdayBit(d), 0)
}

export function weekdaysFromMask(mask: number): Weekday[] {
  return WEEKDAYS.filter((_, i) => (mask & (1 << i)) !== 0)
}

/** ISO weekday of a YYYY-MM-DD date, Monday first (0 = Monday, 6 = Sunday). */
export function isoWeekdayIndex(isoDate: string): number {
  const [y, m, d] = isoDate.split('-').map(Number) as [number, number, number]
  // getUTCDay: 0 = Sunday. Shift so Monday is 0.
  return (new Date(Date.UTC(y, m - 1, d)).getUTCDay() + 6) % 7
}

export function maskIncludesDate(mask: number, isoDate: string): boolean {
  return (mask & (1 << isoWeekdayIndex(isoDate))) !== 0
}

/**
 * Biweekly parity. Weeks are counted from a fixed Monday (2024-01-01); a biweekly row is stamped in even-numbered
 * weeks. Stateless, so two stamps of the same day agree and a row that is re-published keeps its rhythm. A row
 * that needs the other week has no switch in R0 (open question in PRD-017 Q21); the workaround is one weekly row.
 */
export const BIWEEKLY_EPOCH_MONDAY = '2024-01-01'

export function weeksSinceEpoch(isoDate: string): number {
  const day = (iso: string) => {
    const [y, m, d] = iso.split('-').map(Number) as [number, number, number]
    return Date.UTC(y, m - 1, d) / 86_400_000
  }
  return Math.floor((day(isoDate) - day(BIWEEKLY_EPOCH_MONDAY)) / 7)
}

export function isBiweeklyOnWeek(isoDate: string): boolean {
  return weeksSinceEpoch(isoDate) % 2 === 0
}

/**
 * Does a recurring row fall due on this day? Daily rows: every day on the mask. Weekly rows: the mask's day(s)
 * every week. Biweekly rows: the mask's day(s) in on-weeks. A mask of 0 names no day and stamps nothing (the
 * database refuses to store one on a recurring row; this is the defensive reading, never a silent "every day").
 */
export function recurringRowDueOn(row: Pick<TemplateRowLike, 'cadence' | 'weekdayMask'>, isoDate: string): boolean {
  if (!isRecurringCadence(row.cadence))
    return false
  if (!maskIncludesDate(row.weekdayMask, isoDate))
    return false
  return row.cadence === 'biweekly' ? isBiweeklyOnWeek(isoDate) : true
}

/** PRD-020 §3.4 (QA-20): what a file is inside its client. */
export type FileRole = 'operating' | 'holding' | 'management' | 'other'
export const FILE_ROLES: ReadonlyArray<FileRole> = ['operating', 'holding', 'management', 'other']
/** PRD-020 §3.4 (QA-27, ADR 0028): where a file's books live. `none` = a paper entity, legal existence with no books in Gallop. */
export type FileBooks = 'qbo' | 'none'

/**
 * PRD-017 §3.3 (QA-20, ADR 0027): what a template row is stamped on. group = once per client (steps 2–6 of the
 * group close); all_files = every file; files_by_role = files whose role is in `appliesToRoles`.
 */
export type TemplateRowAppliesTo = 'group' | 'all_files' | 'files_by_role'
export const TEMPLATE_ROW_APPLIES_TO: ReadonlyArray<TemplateRowAppliesTo> = ['group', 'all_files', 'files_by_role']

export interface TemplateRowLike {
  readonly id: string
  readonly position: number
  readonly title: string
  readonly cadence: Cadence
  /** QA-32: bitmask over WEEKDAYS for daily / weekly / biweekly rows; 0 on a close-cadence row. */
  readonly weekdayMask: number
  readonly dueOffsetDays: number
  /** 'agent' | 'file_manager' | a principal id. */
  readonly defaultAssignee: string
  readonly dependsOnRowIds: ReadonlyArray<string>
  readonly appliesTo: TemplateRowAppliesTo
  /** Non-empty exactly when appliesTo = files_by_role (the database check says the same). */
  readonly appliesToRoles: ReadonlyArray<string>
  readonly body: string | null
}

export interface OverrideLike {
  readonly templateRowId: string | null
  readonly op: 'hide' | 'add' | 'reassign' | 'due_offset'
  readonly payload: Record<string, unknown>
  /** ADR 0004 three-layer model: which layer wrote it. Omitted = file (the pre-0004 shape). */
  readonly layer?: 'cluster' | 'file'
}

export interface InstantiationContext {
  /** Period bounds as ISO dates (YYYY-MM-DD). */
  readonly periodStartsOn: string
  readonly periodEndsOn: string
  readonly fiscalYearStartMonth: number
  /** The file's role as of this stamp (QA-20). A file that changes role mid-month is re-read at its next stamp. */
  readonly fileRole: FileRole
  /**
   * QA-27 (PRD-017 edge case 62): a paper file (`none`) takes files_by_role rows that name its role and never an
   * all_files row (those are the books rows: bank rec, reconcile, review; a paper entity has none). Omitted = qbo.
   */
  readonly fileBooks?: FileBooks
  readonly agentPrincipalId: string | null
  readonly managerPrincipalId: string | null
  /** Who gets items when neither the agent nor a manager exists (the instantiating person, or the account lead). */
  readonly fallbackPrincipalId: string
  /** Template row ids that already produced a `once` item on this file. */
  readonly onceRowsAlreadyInstantiated: ReadonlySet<string>
}

export interface PlannedItem {
  readonly templateRowId: string | null
  readonly title: string
  readonly body: string | null
  readonly dueOn: string
  readonly assigneePrincipalId: string
  /** True when any override (cluster or file) shaped this item. */
  readonly fileOverride: boolean
  /** The outermost layer that touched the row: file wins over cluster; null when the firm row stands as written. */
  readonly overrideLayer: 'cluster' | 'file' | null
  /** Template row ids this item depends on (resolved to item ids after insert). */
  readonly dependsOnRowIds: ReadonlyArray<string>
}

/** Month index (1..12) of `date` inside a fiscal year that starts in `fyStart`. */
export function fiscalMonthIndex(isoDate: string, fyStart: number): number {
  const month = Number(isoDate.slice(5, 7))
  return ((month - fyStart + 12) % 12) + 1
}

/**
 * PRD-017 §3.7: monthly always; quarterly on FY months 3, 6, 9, 12; annually on FY month 12; once only the first
 * time. A recurring row (daily / weekly / biweekly) never applies to a period: it is stamped by day (QA-32).
 */
export function rowApplies(row: TemplateRowLike, ctx: InstantiationContext): boolean {
  const fm = fiscalMonthIndex(ctx.periodEndsOn, ctx.fiscalYearStartMonth)
  switch (row.cadence) {
    case 'monthly': return true
    case 'quarterly': return fm % 3 === 0
    case 'annually': return fm === 12
    case 'once': return !ctx.onceRowsAlreadyInstantiated.has(row.id)
    case 'daily':
    case 'weekly':
    case 'biweekly':
      return false
  }
}

/**
 * QA-20: does this row target a file of the given role? Group rows never target a file (they belong to the
 * cluster); all_files rows target every file with books; files_by_role rows target the listed roles only. An empty
 * role list on a files_by_role row targets nothing (the API and the database refuse to store one; this is the
 * defensive reading, never a silent "everything").
 *
 * QA-27 (reconciled with QA-20, 2026-09-13): a paper file (`books = none`) is excluded from all_files rows, which
 * are the books rows (a paper entity has no bank to reconcile), and included in files_by_role rows that name its
 * role: a paper holding company still owes its IC-loan and distributions rows. Edge case 62 in PRD-017.
 */
export function rowTargetsFile(row: Pick<TemplateRowLike, 'appliesTo' | 'appliesToRoles'>, fileRole: FileRole, fileBooks: FileBooks = 'qbo'): boolean {
  switch (row.appliesTo) {
    case 'group': return false
    case 'all_files': return fileBooks !== 'none'
    case 'files_by_role': return row.appliesToRoles.includes(fileRole)
  }
}

/** True when a files_by_role row applies to none of the given roles: shown as "not applicable" on the client template tab. */
export function rowNotApplicableTo(row: Pick<TemplateRowLike, 'appliesTo' | 'appliesToRoles'>, fileRoles: ReadonlyArray<FileRole>): boolean {
  return row.appliesTo === 'files_by_role' && !fileRoles.some(r => row.appliesToRoles.includes(r))
}

/** Offset a named day (a period boundary) by calendar days; no clock read. Same definition as `shiftDate` (ADR 0017). */
export function addDays(isoDate: string, days: number): string {
  return shiftDate(isoDate, days)
}

function resolveAssignee(defaultAssignee: string, ctx: Pick<InstantiationContext, 'agentPrincipalId' | 'managerPrincipalId' | 'fallbackPrincipalId'>): string {
  if (defaultAssignee === 'agent')
    return ctx.agentPrincipalId ?? ctx.managerPrincipalId ?? ctx.fallbackPrincipalId
  if (defaultAssignee === 'file_manager')
    return ctx.managerPrincipalId ?? ctx.fallbackPrincipalId
  return defaultAssignee
}

/**
 * Stamp the template as of now plus the file's overrides onto one period. Hidden rows produce nothing
 * (and dependencies on them are dropped); reassign / due_offset overrides change one row; add overrides
 * append file-only items. Group rows are skipped for a file instance (they belong to the cluster); by-role
 * rows are skipped when the file's role is not on the row (QA-20). A file override can still hide a row that
 * would apply; it cannot add a row the role rule excludes (that is an `add` override, a file-only item).
 */
export function planInstantiation(rows: ReadonlyArray<TemplateRowLike>, overrides: ReadonlyArray<OverrideLike>, ctx: InstantiationContext): PlannedItem[] {
  // Overrides arrive cluster first, file after (layeredOverrides); a later entry for the same row wins.
  const layerOf = (o: OverrideLike): 'cluster' | 'file' => o.layer ?? 'file'
  const hidden = new Set(overrides.filter(o => o.op === 'hide' && o.templateRowId).map(o => o.templateRowId!))
  const reassign = new Map(overrides.filter(o => o.op === 'reassign' && o.templateRowId).map(o => [o.templateRowId!, String(o.payload.defaultAssignee ?? 'agent')]))
  const dueOffset = new Map(overrides.filter(o => o.op === 'due_offset' && o.templateRowId).map(o => [o.templateRowId!, Number(o.payload.dueOffsetDays ?? 0)]))
  const touchedBy = new Map<string, 'cluster' | 'file'>()
  for (const o of overrides) {
    if ((o.op === 'reassign' || o.op === 'due_offset') && o.templateRowId) {
      const prev = touchedBy.get(o.templateRowId)
      touchedBy.set(o.templateRowId, prev === 'file' ? 'file' : layerOf(o))
    }
  }
  const out: PlannedItem[] = []
  const kept = new Set<string>()
  for (const row of [...rows].sort((a, b) => a.position - b.position)) {
    if (!rowTargetsFile(row, ctx.fileRole, ctx.fileBooks) || hidden.has(row.id) || !rowApplies(row, ctx))
      continue
    kept.add(row.id)
    const offset = dueOffset.get(row.id) ?? row.dueOffsetDays
    const assignee = reassign.get(row.id) ?? row.defaultAssignee
    out.push({
      templateRowId: row.id,
      title: row.title,
      body: row.body,
      dueOn: addDays(ctx.periodEndsOn, offset),
      assigneePrincipalId: resolveAssignee(assignee, ctx),
      fileOverride: reassign.has(row.id) || dueOffset.has(row.id),
      overrideLayer: touchedBy.get(row.id) ?? null,
      dependsOnRowIds: row.dependsOnRowIds,
    })
  }
  for (const o of overrides) {
    if (o.op !== 'add')
      continue
    const cadence = (typeof o.payload.cadence === 'string' ? o.payload.cadence : 'monthly') as Cadence
    const pseudo: TemplateRowLike = { id: `override`, position: 0, title: String(o.payload.title ?? 'File item'), cadence, weekdayMask: 0, dueOffsetDays: Number(o.payload.dueOffsetDays ?? 0), defaultAssignee: String(o.payload.defaultAssignee ?? 'file_manager'), dependsOnRowIds: [], appliesTo: 'all_files', appliesToRoles: [], body: typeof o.payload.body === 'string' ? o.payload.body : null }
    if (!rowApplies(pseudo, ctx))
      continue
    out.push({ templateRowId: null, title: pseudo.title, body: pseudo.body, dueOn: addDays(ctx.periodEndsOn, pseudo.dueOffsetDays), assigneePrincipalId: resolveAssignee(pseudo.defaultAssignee, ctx), fileOverride: true, overrideLayer: layerOf(o), dependsOnRowIds: [] })
  }
  // Dependencies on rows that did not instantiate this period are dropped (nothing to wait on).
  return out.map(p => ({ ...p, dependsOnRowIds: p.dependsOnRowIds.filter(id => kept.has(id)) }))
}

// ---------------------------------------------------------------------------------------------
// stamp_recurring (QA-32, ADR 0030): the day-bound sibling of planInstantiation
// ---------------------------------------------------------------------------------------------

export interface RecurringContext {
  /** The day being stamped (YYYY-MM-DD): the nightly tick passes tomorrow. */
  readonly dueOn: string
  readonly fileRole: FileRole
  readonly fileBooks?: FileBooks
  readonly agentPrincipalId: string | null
  readonly managerPrincipalId: string | null
  readonly fallbackPrincipalId: string
  /** Template row ids that already have an item on this file for `dueOn` (the idempotency read). */
  readonly alreadyStamped: ReadonlySet<string>
}

export interface PlannedRecurringItem {
  readonly templateRowId: string
  readonly title: string
  readonly body: string | null
  readonly dueOn: string
  readonly assigneePrincipalId: string
  readonly overrideLayer: 'cluster' | 'file' | null
}

/**
 * Which recurring rows land on a file for one day. Role targeting (QA-20), paper files (QA-27) and hide /
 * reassign overrides apply exactly as for the period stamp; due_offset and dependencies do not (a daily item is
 * due on its day, and day-bound items never gate one another). Rows already stamped for the day are skipped, so
 * running the tick twice, or a late tick plus a Close-tab visit, writes one item per (row, file, day).
 */
export function planRecurring(rows: ReadonlyArray<TemplateRowLike>, overrides: ReadonlyArray<OverrideLike>, ctx: RecurringContext): PlannedRecurringItem[] {
  const layerOf = (o: OverrideLike): 'cluster' | 'file' => o.layer ?? 'file'
  const hidden = new Set(overrides.filter(o => o.op === 'hide' && o.templateRowId).map(o => o.templateRowId!))
  const reassign = new Map<string, { assignee: string, layer: 'cluster' | 'file' }>()
  for (const o of overrides) {
    if (o.op === 'reassign' && o.templateRowId) {
      const prev = reassign.get(o.templateRowId)
      reassign.set(o.templateRowId, { assignee: String(o.payload.defaultAssignee ?? 'agent'), layer: prev?.layer === 'file' ? 'file' : layerOf(o) })
    }
  }
  const out: PlannedRecurringItem[] = []
  for (const row of [...rows].sort((a, b) => a.position - b.position)) {
    if (!isRecurringCadence(row.cadence) || !rowTargetsFile(row, ctx.fileRole, ctx.fileBooks) || hidden.has(row.id) || ctx.alreadyStamped.has(row.id) || !recurringRowDueOn(row, ctx.dueOn))
      continue
    const r = reassign.get(row.id)
    out.push({
      templateRowId: row.id,
      title: row.title,
      body: row.body,
      dueOn: ctx.dueOn,
      assigneePrincipalId: resolveAssignee(r?.assignee ?? row.defaultAssignee, { agentPrincipalId: ctx.agentPrincipalId, managerPrincipalId: ctx.managerPrincipalId, fallbackPrincipalId: ctx.fallbackPrincipalId }),
      overrideLayer: r?.layer ?? null,
    })
  }
  return out
}

/**
 * Cycle check for "blocked cannot start until blocking is done" edges (PRD-017 §3.8). Returns the
 * cycle as a path of item ids when adding `candidate` would close one, else null.
 */
export function findCycle(existing: ReadonlyArray<{ blockedItemId: string, blockingItemId: string }>, candidate: { blockedItemId: string, blockingItemId: string }): string[] | null {
  if (candidate.blockedItemId === candidate.blockingItemId)
    return [candidate.blockedItemId, candidate.blockedItemId]
  // Adding blocked ← blocking closes a cycle iff blocking already (transitively) waits on blocked.
  const waitsOn = new Map<string, string[]>()
  for (const e of existing) {
    const list = waitsOn.get(e.blockedItemId) ?? []
    list.push(e.blockingItemId)
    waitsOn.set(e.blockedItemId, list)
  }
  const stack: Array<{ node: string, path: string[] }> = [{ node: candidate.blockingItemId, path: [candidate.blockedItemId, candidate.blockingItemId] }]
  const seen = new Set<string>()
  while (stack.length > 0) {
    const { node, path } = stack.pop()!
    if (seen.has(node))
      continue
    seen.add(node)
    for (const next of waitsOn.get(node) ?? []) {
      if (next === candidate.blockedItemId)
        return [...path, next]
      stack.push({ node: next, path: [...path, next] })
    }
  }
  return null
}

/** PRD-017 §3.2: "waiting on <item> (<file>)". */
export function dependencyReason(upstreamTitle: string, fileName: string | null): string {
  return `waiting on ${upstreamTitle}${fileName ? ` (${fileName})` : ''}`
}

export interface PercentInput { readonly status: string, readonly doneOutcome: string | null, readonly parentId: string | null }

/**
 * PRD-017 §3.2: Done over all items on the instance, excluding items deferred with a reason. Sub-tasks are
 * excluded (the parent carries them). Empty instance = 0.
 */
export function percentComplete(items: ReadonlyArray<PercentInput>): number {
  const top = items.filter(i => i.parentId === null && i.doneOutcome !== 'deferred')
  if (top.length === 0)
    return 0
  const done = top.filter(i => i.status === 'done').length
  return Math.round((done / top.length) * 100)
}

export interface ChildLike { readonly id: string, readonly status: string, readonly isVerification: boolean, readonly doneOutcome: string | null }

export interface ParentDerivation {
  readonly total: number
  readonly done: number
  readonly allSiblingsDone: boolean
  readonly verificationDone: boolean
  /** True when the parent may be marked Done (every child incl. the verification check is done). */
  readonly canComplete: boolean
  /** Rule outcome when the verification child completes: the parent closes automatically. */
  readonly autoComplete: boolean
  /** The verification child may start only after every sibling is done. */
  readonly verificationReady: boolean
}

/** agent-standards #1: the parent cannot reach Done until the final agent verification sub-task passes. */
export function deriveParent(children: ReadonlyArray<ChildLike>): ParentDerivation {
  const siblings = children.filter(c => !c.isVerification)
  const verification = children.find(c => c.isVerification) ?? null
  const done = children.filter(c => c.status === 'done').length
  const allSiblingsDone = siblings.every(c => c.status === 'done')
  const verificationDone = verification !== null && verification.status === 'done' && verification.doneOutcome !== 'deferred'
  const canComplete = children.length === 0 || (allSiblingsDone && verificationDone)
  return {
    total: children.length,
    done,
    allSiblingsDone,
    verificationDone,
    canComplete,
    autoComplete: children.length > 0 && canComplete,
    verificationReady: siblings.length > 0 && allSiblingsDone,
  }
}

/** Title for the mandatory verification sub-task. */
export function verificationTitle(parentTitle: string): string {
  return `Verify: ${parentTitle}`
}
